import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';

import { ModalTitle } from 'components';
import { useAppSelector } from 'store';
import { selectWaitingRoomProfile } from 'store/waitingRoom';
import { getProviderFullName } from 'utils';
import { isHipaaLink } from 'config/productProfile';

import styles from './MakePaymentDialog.module.scss';

export interface MakePaymentDialogProps extends DialogProps {
  clientSecret: string;
  amount: number;
  description: string | null;
  onClose(): void;
}

export function MakePaymentDialog({ onClose, clientSecret, amount, description, ...rest }: MakePaymentDialogProps) {
  const { t } = useTranslation('providerRoom');
  const waitingRoomProfile = useAppSelector(selectWaitingRoomProfile);

  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRequestSent, setIsRequestSent] = useState<boolean>(false);

  const formRef = useRef<HTMLButtonElement>(null);

  const formattedAmount: string = useMemo(() => {
    return '$' + amount.toFixed(2);
  }, [amount]);

  useEffect(() => {
    if (!stripe || !isRequestSent || !clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent?.status) {
        case 'succeeded':
          // payment completed successfully
          break;
        case 'processing':
          setIsLoading(true);
          break;
        default:
          setMessage(t('somethingWentWrong'));
          break;
      }
    });
  }, [stripe, clientSecret, isRequestSent, t]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: '',
      },
      redirect: 'if_required',
    });

    if (paymentIntent?.status === 'succeeded') {
      // payment completed successfully
    }

    if (error) {
      setMessage(error.message ?? null);
    }

    setIsRequestSent(true);
    setIsLoading(false);
  };

  const handleClose = () => {
    onClose();
  };

  const handlePay = () => {
    formRef.current?.click();
  };

  return (
    <Dialog {...rest} className={styles.dialog}>
      <DialogTitle>
        <ModalTitle title={t('payment')} onClose={handleClose} />
      </DialogTitle>

      <DialogContent>
        {message && (
          <Box className={styles.error}>
            <Typography variant='body1' color='white'>
              {message}
            </Typography>
          </Box>
        )}

        <Stack>
          <Typography variant='body4'>{isHipaaLink ? t('providerOrganization') : t('organization')}</Typography>

          <Typography variant='body2'>{getProviderFullName(waitingRoomProfile)}</Typography>

          <Typography variant='body2'>{waitingRoomProfile?.organization}</Typography>
        </Stack>

        <Stack>
          <Typography variant='body4'>{t('amountLabel')}</Typography>

          <Typography variant='body2'>{formattedAmount}</Typography>
        </Stack>

        {description && (
          <Stack>
            <Typography variant='body4'>{t('reasonForPayment')}</Typography>

            <Typography variant='body2'>{description}</Typography>
          </Stack>
        )}

        <form onSubmit={handleSubmit}>
          <PaymentElement id='payment-element' options={{ layout: 'tabs' }} />
          <button hidden ref={formRef} type='submit'></button>
        </form>

        <Stack className={styles.formButton}>
          <Button variant='outlined' onClick={handleClose}>
            {t('decline')}
          </Button>

          <Button variant='contained' disabled={isLoading || !stripe || !elements} onClick={handlePay}>
            {t('payWithAmount', { amount: formattedAmount })}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
